import { Modal, Table, Tag, Button, Space, notification } from "antd"
import { FC, useState } from "react"
import { ContestPeriodGame, GameChallenge, deleteGameChallenge } from "../../../api"
import { defaultFormat } from "utils/date"
import { Column } from "abstract/types/column.type"
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { showDeleteConfirm } from "components"
import { ChallengeModal } from "./ChallengeModal"

interface ChallengesModalProps {
  visible: boolean;
  onClose: () => void;
  selectedGame: ContestPeriodGame | null;
  challenges: GameChallenge[];
  loading: boolean;
  onRefreshChallenges: () => void;
}

export const ChallengesModal: FC<ChallengesModalProps> = ({
  visible,
  onClose,
  selectedGame,
  challenges,
  loading,
  onRefreshChallenges,
}) => {
  const [isChallengeModalVisible, setIsChallengeModalVisible] = useState(false)
  const [challengeModalType, setChallengeModalType] = useState<"create" | "edit">("create")
  const [editingChallenge, setEditingChallenge] = useState<GameChallenge | null>(null)

  const handleCreateChallenge = () => {
    setChallengeModalType("create")
    setEditingChallenge(null)
    setIsChallengeModalVisible(true)
  }

  const handleEditChallenge = (challenge: GameChallenge) => {
    setChallengeModalType("edit")
    setEditingChallenge(challenge)
    setIsChallengeModalVisible(true)
  }

  const handleDeleteChallenge = async(challenge: GameChallenge) => {
    try {
      await deleteGameChallenge(challenge.id)
      notification.success({ message: "Challenge deleted successfully" })
      onRefreshChallenges()
    } catch (error) {
      console.error("Failed to delete challenge:", error)
      notification.error({ message: "Failed to delete challenge" })
    }
  }

  const onCloseChallengeModal = () => {
    setIsChallengeModalVisible(false)
    setEditingChallenge(null)
  }

  const challengesColumns: Column[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (id: string) => (
        <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
          {id}
        </div>
      ),
      width: 250,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 180,
    },
    {
      title: "Action Text",
      dataIndex: "actionText",
      key: "actionText",
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={type === "ACCUMULATIVE" ? "blue" : "orange"}>
          {type}
        </Tag>
      ),
      width: 130,
    },
    {
      title: "Goal",
      dataIndex: "goal",
      key: "goal",
      width: 80,
    },
    {
      title: "Identifier",
      dataIndex: "identifier",
      key: "identifier",
      width: 120,
    },
    {
      title: "Target Value",
      dataIndex: "targetValue",
      key: "targetValue",
      render: (value: number | null) => value ?? "N/A",
      width: 100,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => (
        <Tag color="green">{defaultFormat(new Date(createdAt))}</Tag>
      ),
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      dataIndex: "actions",
      render: (_, challenge: GameChallenge) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditChallenge(challenge)}
          >
            Edit
          </Button>
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => {
              showDeleteConfirm(`challenge: ${challenge.title}`, undefined, () => handleDeleteChallenge(challenge))
            }}
          >
            Delete
          </Button>
        </Space>
      ),
      width: 150,
    },
  ]

  return (
    <>
      <Modal
        visible={visible}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Challenges for Game: {selectedGame?.gameId}</span>
            <Button
              style={{ marginRight: 24 }}
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateChallenge}
            >
              Create Challenge
            </Button>
          </div>
        }
        width="1400px"
        footer={null}
        onCancel={onClose}
        closeIcon={<ArrowLeftOutlined />}
        zIndex={1100}
      >
        <Table
          dataSource={challenges}
          columns={challengesColumns as any}
          rowKey={record => `${record.id}-${record.createdAt}`}
          loading={loading}
          pagination={false}
        />
      </Modal>

      <ChallengeModal
        visible={isChallengeModalVisible}
        onClose={onCloseChallengeModal}
        editType={challengeModalType}
        contestPeriodId={selectedGame?.contestPeriodId || ""}
        gameId={selectedGame?.gameId || 0}
        editingChallenge={editingChallenge}
        loading={loading}
        onSuccess={onRefreshChallenges}
      />
    </>
  )
}
